import { lazy } from "react";
import { RouteConfig } from "@/types/navigation.types";
import { User_Role } from "@shared/constants/api";
import {
  LayoutDashboard,
  Users,
  UserCog,
  Briefcase,
  MapPin,
  Video,
  Wallet,
  BadgeIndianRupee,
  BellIcon,
  Settings,
  Sparkle,
  File,
  Backpack,
  Phone,
} from "lucide-react";

// Lazy-loaded route components
const Dashboard = lazy(() => import("@features/Dashboard/Dashboard"));
const CustomerManagement = lazy(() => import("@features/customer/Customer"));
const DeleteCustomer = lazy(() => import("@features/customer/DeleteCustomer"));
const CustomerWallet = lazy(() => import("@features/wallet/CustomerWallet"));

const Curator = lazy(() => import("@features/curator/Curator"));
const DeleteCurator = lazy(() => import("@features/curator/DeleteCurator"));

const Agent = lazy(() => import("@features/agent/Agent"));
const DeleteAgent = lazy(() => import("@features/agent/DeleteAgent"));

const Itinerary = lazy(() => import("@features/itinerary/Itinerary"));
const ItineraryBuilder = lazy(() => import("@features/itinerary/components/ItineraryBuilder"));
const ItineraryDetailsPage = lazy(
  () => import("@features/itinerary/components/ItineraryDetailsPage")
);
const DeleteItinerary = lazy(() => import("@features/itinerary/DeleteItinerary"));

const AiDashboard = lazy(() => import("@features/ai/AiDashboard"));
const AiDetailsPage = lazy(() => import("@features/ai/AiDetailsPage"));
const AssignAgentCategory = lazy(() => import("@/features/ai/AssignAgentCategory"));

const OfferManagement = lazy(() => import("@features/offer/OfferManagement"));
const DeletedOfferManagement = lazy(() => import("@features/offer/DeleteOfferData"));
const ClaimOfferManagement = lazy(() => import("@features/offer/ClaimOfferData"));

const CashBack = lazy(() => import("@features/wallet/CashBack"));
const WalletTransaction = lazy(() => import("@features/wallet/WalletTransaction"));

const Notification = lazy(() => import("@features/notification/Notification"));

// Settings pages
const SystemSettings = lazy(() => import("@features/setting/pages/SystemSettings"));
const CashbackSettings = lazy(() => import("@features/setting/pages/CashbackSettings"));
const RoleManagement = lazy(() => import("@features/setting/pages/RoleManagement"));
const PolicyManagement = lazy(() => import("@features/setting/pages/PolicyManagement"));
const CountryFlags = lazy(() => import("@features/setting/pages/CountryFlags"));
const GroupPackageTypes = lazy(() => import("@features/setting/pages/GroupPackageTypes"));

const Profile = lazy(() => import("@features/Profile/Profile"));

const AssignedItinerary = lazy(() => import("@features/agent/AssignedItinerary"));
const TravelItineraryForm = lazy(() => import("@features/agent/component/AssignedItineraryEdit"));

const VideoCallPage = lazy(() => import("@features/call/pages/CallListingPage"));
const CuratorCallPage = lazy(() => import("@features/call/pages/CuratorCallPage"));
const GroupPackage = lazy(() => import("@features/groupPackage/GroupPackage"));
const DeleteGroupPackage = lazy(() => import("@features/groupPackage/DeleteGroupPackage"));
const GroupPackageBuilder = lazy(
  () => import("@features/groupPackage/components/GroupPackageBuilder")
);
const PackageDetailsViewPage = lazy(
  () => import("@features/groupPackage/components/PackageDetailsViewPage")
);

export const routeConfig: RouteConfig[] = [
  {
    path: "/dashboard",
    component: Dashboard,
    roles: [User_Role.admin, User_Role.curator, User_Role.agent],
    sidebar: {
      label: (role) => {
        switch (role) {
          case User_Role.admin:
            return "Admin Dashboard";
          case User_Role.curator:
            return "Curator Dashboard";
          case User_Role.agent:
            return "Agent Dashboard";
          default:
            return "Dashboard";
        }
      },
      icon: LayoutDashboard,
      order: 1,
    },
  },

  {
    path: "/customers",
    component: CustomerManagement,
    roles: [User_Role.admin],
    sidebar: {
      label: "Customer Management",
      icon: Users,
      order: 2,
    },
  },
  {
    path: "/customers/trash",
    component: DeleteCustomer,
    roles: [User_Role.admin],
  },

  {
    path: "/curators",
    component: Curator,
    roles: [User_Role.admin],
    sidebar: {
      label: "Curator Management",
      icon: UserCog,
      order: 3,
    },
  },
  {
    path: "/curators/trash",
    component: DeleteCurator,
    roles: [User_Role.admin],
  },

  {
    path: "/agents",
    component: Agent,
    roles: [User_Role.admin],
    sidebar: {
      label: "Agent Management",
      icon: Briefcase,
      order: 4,
    },
  },
  {
    path: "/agents/trash",
    component: DeleteAgent,
    roles: [User_Role.admin],
  },

  {
    path: "/itinerary",
    component: Itinerary,
    roles: [User_Role.admin, User_Role.curator],
    sidebar: {
      label: "Itinerary Management",
      icon: MapPin,
      order: 5,
    },
  },
  {
    path: "/itinerary/trash",
    component: DeleteItinerary,
    roles: [User_Role.admin],
  },
  {
    path: "/itinerary/add",
    component: ItineraryBuilder,
    roles: [User_Role.admin, User_Role.curator],
  },
  {
    path: "/itinerary/add/:id",
    component: ItineraryBuilder,
    roles: [User_Role.admin, User_Role.curator],
  },
  {
    path: "/itinerary/details/:id",
    component: ItineraryDetailsPage,
    roles: [User_Role.admin, User_Role.curator],
  },
  {
    path: "/group-package",
    component: GroupPackage,
    roles: [User_Role.admin, User_Role.curator],
    sidebar: {
      label: "Group Packages",
      icon: Backpack,
      order: 6,
    },
  },
  {
    path: "/group-package/trash",
    component: DeleteGroupPackage,
    roles: [User_Role.admin],
  },
  {
    path: "/group-package/add",
    component: GroupPackageBuilder,
    roles: [User_Role.admin, User_Role.curator],
  },
  {
    path: "/group-package/add/:id",
    component: GroupPackageBuilder,
    roles: [User_Role.admin, User_Role.curator],
  },
  {
    path: "/group-package/details/:id",
    component: PackageDetailsViewPage,
    roles: [User_Role.admin, User_Role.curator],
  },

  {
    path: "/ai_dashboard",
    component: AiDashboard,
    roles: [User_Role.admin, User_Role.curator],
    sidebar: {
      label: "AI Requests",
      icon: Sparkle,
      order: 7,
    },
  },

  {
    path: "/ai_dashboard/details/:id",
    component: AiDetailsPage,
    roles: [User_Role.admin, User_Role.curator, User_Role.agent],
  },

  {
    path: "/assign_agent_category/:requestId",
    component: AssignAgentCategory,
    roles: [User_Role.admin],
  },

  {
    path: "/video",
    component: VideoCallPage,
    roles: [User_Role.admin],
    sidebar: {
      label: "Live Monitor", // Changed to reflect Admin nature
      icon: Video,
      order: 8,
    },
  },
  {
    path: "/assigned-calls",
    component: CuratorCallPage,
    roles: [User_Role.curator],
    sidebar: {
      label: "My Calls",
      icon: Phone,
      order: 8,
    }
  },

  {
    path: "/customer/wallet",
    component: CustomerWallet,
    roles: [User_Role.admin],
    sidebar: {
      label: "Wallet Management",
      icon: Wallet,
      order: 9,
    },
    children: [
      {
        path: "/customer/wallet",
        component: CustomerWallet,
        roles: [User_Role.admin],
        sidebar: {
          label: "Customer Wallet",
          order: 1,
        },
      },
      {
        path: "/customer/cashback",
        component: CashBack,
        roles: [User_Role.admin],
        sidebar: {
          label: "Cashback Management",
          order: 2,
        },
      },
      {
        path: "/transactions",
        component: WalletTransaction,
        roles: [User_Role.admin],
        sidebar: {
          label: "Wallet Transaction",
          order: 3,
        },
      },
    ],
  },

  {
    path: "/offer",
    component: OfferManagement,
    roles: [User_Role.admin],
    sidebar: {
      label: "Offer Management",
      icon: BadgeIndianRupee,
      order: 10,
    },
  },
  {
    path: "/offer/trash",
    component: DeletedOfferManagement,
    roles: [User_Role.admin],
  },
  {
    path: "/offer/claimed",
    component: ClaimOfferManagement,
    roles: [User_Role.admin],
  },

  {
    path: "/notifications",
    component: Notification,
    roles: [User_Role.admin, User_Role.curator, User_Role.agent],
    sidebar: {
      label: "Notifications",
      icon: BellIcon,
      order: 11,
    },
  },

  // {
  //   path: '/reports',
  //   component: ReportsPage,
  //   roles: [User_Role.admin],
  //   sidebar: {
  //     label: 'Report & Analytics',
  //     icon: BarChart,
  //     order: 12,
  //   },
  // },

  {
    path: "/settings/system",
    component: SystemSettings,
    roles: [User_Role.admin],
    sidebar: {
      label: "Settings",
      icon: Settings,
      order: 13,
    },
    children: [
      {
        path: "/settings/system",
        component: SystemSettings,
        roles: [User_Role.admin],
        sidebar: {
          label: "System Settings",
          order: 1,
          showInSidebar: false,
        },
      },
      {
        path: "/settings/cashback",
        component: CashbackSettings,
        roles: [User_Role.admin],
        sidebar: {
          label: "Cashback Settings",
          order: 2,
        },
      },
      {
        path: "/settings/role",
        component: RoleManagement,
        roles: [User_Role.admin],
        sidebar: {
          label: "Role Management",
          order: 3,
        },
      },
      {
        path: "/settings/policy",
        component: PolicyManagement,
        roles: [User_Role.admin],
        sidebar: {
          label: "Policy Management",
          order: 4,
        },
      },
      {
        path: "/settings/country-flags",
        component: CountryFlags,
        roles: [User_Role.admin],
        sidebar: {
          label: "Country Flags",
          order: 5,
        },
      },
      {
        path: "/settings/package-types",
        component: GroupPackageTypes,
        roles: [User_Role.admin],
        sidebar: {
          label: "GroupPackage Types",
          order: 6,
        },
      },
    ],
  },

  {
    path: "/profile",
    component: Profile,
    roles: [User_Role.admin, User_Role.curator, User_Role.agent],
  },

  {
    path: "/assigned-itinerary",
    component: AssignedItinerary,
    roles: [User_Role.agent],
    sidebar: {
      label: "Assigned Itinerary",
      icon: File,
      order: 14,
    },
  },
  {
    path: "/assigned-itinerary/edit/:id",
    component: TravelItineraryForm,
    roles: [User_Role.agent],
  },
];
